import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

/**
 * Hooks für den Soll-Versionen-Editor (Sprint 4, WT-404).
 *
 * Backend-Endpunkte:
 *   GET /version-targets                        -> Liste aller Service-Types mit Target
 *   PUT /version-targets/{service_type}         -> Body { target_version, eol_at?, notes? }
 *
 * Note: das Stub-OpenAPI hatte ursprünglich `/version-targets/{service_type}/{version}`,
 * der Sprint-4-Auftrag verlangt aber den simpleren PUT-Pfad ohne version-Segment
 * (ein Target je Service-Type, History via Audit). Wir halten die Frontend-API
 * konform zum Auftrag - Backend zieht in Sprint 4 nach.
 */

export interface VersionTargetRow {
  service_type: string;
  service_type_label?: string | null;
  target_version: string | null;
  eol_at?: string | null;
  notes?: string | null;
  /** Wie viele Systeme haben diesen Service-Type (regardless of drift). */
  systems_total: number;
  /** Wie viele davon laufen einer älteren Version. */
  systems_drifted: number;
  updated_at?: string | null;
}

export interface VersionTargetsResponse {
  data: VersionTargetRow[];
}

export interface UpdateVersionTargetInput {
  serviceType: string;
  targetVersion: string;
  eolAt?: string | null;
  notes?: string | null;
}

interface UpdateVersionTargetBody {
  target_version: string;
  eol_at?: string | null;
  notes?: string | null;
}

export const versionTargetsKeys = {
  all: ['version-targets'] as const,
  list: ['version-targets', 'list'] as const,
};

export function useVersionTargets(): ReturnType<
  typeof useQuery<VersionTargetsResponse>
> {
  return useQuery<VersionTargetsResponse>({
    queryKey: versionTargetsKeys.list,
    queryFn: () => api.get<VersionTargetsResponse>('/version-targets'),
    staleTime: 30_000,
  });
}

export function useUpdateVersionTarget(): ReturnType<
  typeof useMutation<VersionTargetRow, Error, UpdateVersionTargetInput>
> {
  const qc = useQueryClient();
  return useMutation<VersionTargetRow, Error, UpdateVersionTargetInput>({
    mutationFn: ({ serviceType, targetVersion, eolAt, notes }) => {
      const body: UpdateVersionTargetBody = { target_version: targetVersion };
      if (eolAt !== undefined) body.eol_at = eolAt;
      if (notes !== undefined) body.notes = notes;
      return api.put<VersionTargetRow, UpdateVersionTargetBody>(
        `/version-targets/${encodeURIComponent(serviceType)}`,
        body
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: versionTargetsKeys.all });
      // Drift-Report hängt direkt am Target -> mit-invalidieren.
      void qc.invalidateQueries({ queryKey: ['drift'] });
    },
  });
}
